<template>
  <div
    class="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
    style="padding-bottom: calc(4rem + env(safe-area-inset-bottom))"
    @click.self="close"
  >
    <form
      class="w-full max-h-[85dvh] flex flex-col bg-white rounded-2xl shadow-xl overflow-hidden"
      @submit.prevent="save"
    >
      <div class="flex items-center justify-between px-4 py-3 bg-dark-primary">
        <h2 class="text-lg font-semibold text-white">Edit notes</h2>
        <button type="button" class="text-2xl leading-none text-white px-2" @click="close">×</button>
      </div>

      <div class="flex-1 overflow-y-auto p-4 space-y-4">
        <label class="block space-y-1">
          <div class="font-semibold text-gray-800">Account Manager Notes</div>
          <textarea v-model="notes.account_manager_research_notes" rows="8" :class="fieldClass('account_manager_research_notes')"></textarea>
        </label>

        <!-- Feedback is recorded against the company's campaign, so it can only be given once it has one -->
        <label v-if="company.campaign" class="block space-y-1">
          <div class="font-semibold text-gray-800">Client Feedback</div>
          <select v-model="notes.client_feedback" :class="fieldClass('client_feedback')">
            <option :value="null">--</option>
            <option v-for="option in clientFeedbackOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </select>
        </label>

        <div v-if="company.campaign" class="space-y-1">
          <div class="font-semibold text-gray-800">Client Feedback Notes</div>
          <!-- Edits and deletes to existing notes save straight away -->
          <ClientFeedbackNotesList :company="company" :allowAdd="false" @refetch="emit('notes-changed')" />
          <textarea v-model="notes.client_feedback_notes" rows="4" placeholder="Add a note" :class="fieldClass('client_feedback_notes')"></textarea>
        </div>
      </div>

      <!-- Outside the scrolling body, so Save and Cancel stay in view -->
      <div class="px-4 py-2 space-y-2 border-t border-gray-200">
        <div v-if="errorMessage" class="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm p-3">{{ errorMessage }}</div>
        <div class="grid grid-cols-2 gap-2">
          <button type="button" class="py-2 rounded-md border border-gray-300 text-gray-700" @click="close">Cancel</button>
          <button type="submit" class="py-2 rounded-md bg-dark-primary text-white disabled:opacity-50" :disabled="isSaving">
            {{ isSaving ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import { useStore } from 'vuex';
import { updateCompany, createCompanyClientFeedback, createCompanyClientFeedbackNote, clientFeedbackErrorMessage } from '@/services/Company';
import ClientFeedbackNotesList from '@/components/company-cell-components/ClientFeedbackNotesList.vue';

const props = defineProps({
  company: Object
})

const emit = defineEmits(['close', 'notes-saved', 'notes-changed'])

const store = useStore()

const inputBaseClass = 'appearance-none w-full rounded-md border px-3 py-2 text-base'
const inputClass = `${inputBaseClass} border-gray-300 bg-white`
const changedInputClass = `${inputBaseClass} border-yellow-400 bg-yellow-100`

function fieldClass(field) {
  return field in changes.value ? changedInputClass : inputClass
}

// Same choices as the desktop Edit Company form (CompanyForm.vue)
const clientFeedbackOptions = [
  { value: 'approved', label: 'Approved' },
  { value: 'excluded', label: 'Excluded' },
  { value: 'hold', label: 'Hold' },
  { value: 'pass', label: 'Pass' },
  { value: 'research_further', label: 'Research Further' },
]

// The latest feedback recorded for the company's current client
const initialFeedback = clientFeedbackOptions.some(option => option.value === props.company.current_client_feedback) ? props.company.current_client_feedback : null

const initialNotes = {
  account_manager_research_notes: props.company.account_manager_research_notes || '',
  client_feedback: initialFeedback,
  // A new note to add; existing notes are listed above it
  client_feedback_notes: '',
}
const notes = ref({ ...initialNotes })

const isSaving = ref(false)
const errorMessage = ref('')

const changes = computed(() => {
  const changed = {}
  Object.keys(initialNotes).forEach(field => {
    if (notes.value[field] !== initialNotes[field]) changed[field] = notes.value[field]
  })
  return changed
})
const hasChanges = computed(() => Object.keys(changes.value).length > 0)

function close() {
  if (isSaving.value) return
  if (hasChanges.value && !confirm('Exiting without saving will discard changes.')) return
  emit('close')
}

// Sends only the changed fields, so it can't overwrite the others
async function save() {
  if (!hasChanges.value) {
    emit('close')
    return
  }

  errorMessage.value = ''
  isSaving.value = true
  const token = store.getters['auth/showToken']
  // Client feedback and its notes are recorded as history rows against the company's
  // campaign; everything else is saved on the company.
  // eslint-disable-next-line no-unused-vars
  const { client_feedback, client_feedback_notes, ...companyChanges } = changes.value

  if (Object.keys(companyChanges).length > 0) {
    const response = await updateCompany(props.company.id, companyChanges, token)
    if (!response || response.status !== 200) {
      isSaving.value = false
      const detail = response && response.response && response.response.data && response.response.data.detail
      errorMessage.value = detail ? `Error: Notes were not saved. ${detail}` : 'Error: Notes were not saved. Check your connection and retry'
      return
    }
  }

  const saved = { ...companyChanges }
  const failures = []
  if ('client_feedback' in changes.value) {
    const result = await createCompanyClientFeedback(props.company.id, changes.value.client_feedback ?? '', token)
    if (result && result.status === 201) {
      saved.current_client_feedback = changes.value.client_feedback
    } else {
      failures.push(clientFeedbackErrorMessage(result))
    }
  }
  if ((changes.value.client_feedback_notes || '').trim()) {
    const result = await createCompanyClientFeedbackNote(props.company.id, JSON.stringify({ notes: changes.value.client_feedback_notes }), token)
    if (!result || result.status !== 201) {
      failures.push(clientFeedbackErrorMessage(result))
    }
  }
  isSaving.value = false

  if (failures.length === 0) {
    emit('notes-saved', saved)
    return
  }
  errorMessage.value = `Error: Client feedback was not saved. ${failures.join(' ')}`
}
</script>
